import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Textarea } from 'src/design/atoms/Textarea';
import { IconX } from '@tabler/icons-react';
import React, { useState } from 'react';
import {
  BasicActionIcon,
  IBasicActionIconProps,
} from 'src/design/atoms/BasicActionIcon';
import { DangerButton } from 'src/design/atoms/DangerButton';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';
import { Modal } from 'src/design/molecules/Modal';

export type IRejectTimesheetActionIconProps = Omit<
  IBasicActionIconProps,
  'onClick' | 'children'
> & {
  confirmationTitle?: string;
  confirmationText?: string;
  onReject: (justification: string) => void | Promise<void>;
};

const DefaultProps: Partial<IRejectTimesheetActionIconProps> = {
  variant: 'light',
  color: 'red',
  label: 'Reject',
};

export const RejectTimesheetActionIcon = React.forwardRef<
  HTMLButtonElement,
  IRejectTimesheetActionIconProps
>(
  (
    {
      confirmationTitle = 'Reject Timesheet',
      confirmationText = 'Please provide a justification for rejecting this timesheet:',
      onReject,
      ...props
    },
    ref,
  ) => {
    const [justification, setJustification] = useState('');
    // Confirmation is not optional here: the modal is the only place the
    // required justification is collected, so requestOrRun must always open it.
    const { opened, loading, requestOrRun, confirm, close } =
      useConfirmableAction(onReject, true);

    const handleClose = () => {
      close();
      setJustification('');
    };

    return (
      <>
        <BasicActionIcon
          {...DefaultProps}
          {...props}
          ref={ref}
          onClick={() => requestOrRun('')}
        >
          <IconX size={20} />
        </BasicActionIcon>
        <Modal
          title={confirmationTitle}
          opened={opened}
          onClose={handleClose}
          actions={[
            <DangerButton
              key="reject"
              loading={loading}
              onClick={async () => {
                if (await confirm(justification)) setJustification('');
              }}
              disabled={!justification.trim()}
            >
              Reject
            </DangerButton>,
          ]}
        >
          <Stack gap="md">
            <Text>{confirmationText}</Text>
            <Textarea
              label="Justification"
              placeholder="Enter rejection reason..."
              value={justification}
              onChange={(e) => setJustification(e.currentTarget.value)}
              minRows={3}
              required
              autoFocus
            />
          </Stack>
        </Modal>
      </>
    );
  },
);
RejectTimesheetActionIcon.displayName = 'RejectTimesheetActionIcon';
